import React, { useState, useEffect } from 'react'
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Button,
    Alert,
    CircularProgress,
    Paper,
    Grid,
    FormControlLabel,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { sessionDebugAPI, userSnapshotAPI, userAPI } from '../services/api'

function Debug() {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [success, setSuccess] = useState(null)
    const [selectedSessionId, setSelectedSessionId] = useState('')
    const [currentDay, setCurrentDay] = useState(null)
    const [useNullDay, setUseNullDay] = useState(false)
    const [recentSessions, setRecentSessions] = useState([])
    const [currentDebugValues, setCurrentDebugValues] = useState(null)

    // User Snapshot state
    const [selectedUserId, setSelectedUserId] = useState('')
    const [userSnapshot, setUserSnapshot] = useState(null)
    const [snapshotLoading, setSnapshotLoading] = useState(false)
    const [snapshotError, setSnapshotError] = useState(null)
    const [users, setUsers] = useState([])
    const [usersLoading, setUsersLoading] = useState(false)

    // Clear User Data state
    const [clearDataDialogOpen, setClearDataDialogOpen] = useState(false)
    const [clearDataLoading, setClearDataLoading] = useState(false)
    const [clearDataError, setClearDataError] = useState(null)
    const [clearDataSuccess, setClearDataSuccess] = useState(null)

    useEffect(() => {
        fetchRecentSessions()
        fetchUsers()
    }, [])

    useEffect(() => {
        if (selectedSessionId) {
            fetchCurrentDebugValues()
        } else {
            setCurrentDebugValues(null)
            setCurrentDay(null)
            setUseNullDay(false)
        }
    }, [selectedSessionId])

    useEffect(() => {
        if (selectedUserId) {
            setUserSnapshot(null)
            setSnapshotError(null)
        }
    }, [selectedUserId])

    const fetchUsers = async () => {
        try {
            setUsersLoading(true)
            const response = await userAPI.getUsers()
            setUsers(response.data)
        } catch (err) {
            console.error('Error fetching users:', err)
            // Don't set error for users since it's not critical for the main functionality
        } finally {
            setUsersLoading(false)
        }
    }

    const fetchRecentSessions = async () => {
        try {
            setLoading(true)
            const response = await sessionDebugAPI.getRecentSessions()
            setRecentSessions(response.data)
        } catch (err) {
            console.error('Error fetching recent sessions:', err)
            setError('Failed to fetch recent sessions')
        } finally {
            setLoading(false)
        }
    }

    const fetchCurrentDebugValues = async () => {
        try {
            setLoading(true)
            const response = await sessionDebugAPI.getDebugValues(selectedSessionId)
            const debugValues = response.data.debug_values

            setCurrentDebugValues(debugValues)

            if (debugValues && debugValues.current_day) {
                setCurrentDay(new Date(debugValues.current_day))
                setUseNullDay(false)
            } else {
                setCurrentDay(null)
                setUseNullDay(debugValues && debugValues.current_day === null)
            }
        } catch (err) {
            console.error('Error fetching current debug values:', err)
            setError('Failed to fetch current debug values')
            setCurrentDebugValues(null)
            setCurrentDay(null)
            setUseNullDay(false)
        } finally {
            setLoading(false)
        }
    }

    const handleSaveDebugValues = async () => {
        try {
            setLoading(true)
            setError(null)
            setSuccess(null)

            if (!selectedSessionId) {
                setError('Please select a session ID')
                return
            }

            const debugValues = {
                current_day: useNullDay ? null : currentDay?.toISOString() // Save as UTC ISO string with timezone info
            }

            await sessionDebugAPI.setDebugValues(selectedSessionId, debugValues)

            setSuccess('Debug values saved successfully')
        } catch (err) {
            console.error('Error saving debug values:', err)
            setError('Failed to save debug values')
        } finally {
            setLoading(false)
        }
    }

    const handleClearDebugValues = async () => {
        try {
            setLoading(true)
            setError(null)
            setSuccess(null)

            if (!selectedSessionId) {
                setError('Please select a session ID')
                return
            }

            await sessionDebugAPI.deleteDebugValues(selectedSessionId)

            setCurrentDay(null)
            setUseNullDay(false)
            setSuccess('Debug values cleared successfully')
        } catch (err) {
            console.error('Error clearing debug values:', err)
            setError('Failed to clear debug values')
        } finally {
            setLoading(false)
        }
    }

    const fetchUserSnapshot = async () => {
        try {
            setSnapshotLoading(true)
            setSnapshotError(null)
            setUserSnapshot(null)

            if (!selectedUserId) {
                setSnapshotError('Please select a user')
                return
            }

            const response = await userSnapshotAPI.getUserSnapshotById(selectedUserId)
            setUserSnapshot(response.data)
        } catch (err) {
            console.error('Error fetching user snapshot:', err)
            setSnapshotError('Failed to fetch user snapshot')
            setUserSnapshot(null)
        } finally {
            setSnapshotLoading(false)
        }
    }

    const handleClearUserData = async () => {
        try {
            setClearDataLoading(true)
            setClearDataError(null)
            setClearDataSuccess(null)

            if (!selectedUserId) {
                setClearDataError('Please select a user')
                return
            }

            const response = await userAPI.clearUserData(selectedUserId)

            setClearDataSuccess(`User data cleared successfully! Deleted: ${response.data.summary.milestonesDeleted} health data entries, ${response.data.summary.assessmentsDeleted} assessment results`)

            // Close the dialog
            setClearDataDialogOpen(false)

            // Refresh the user snapshot if it's currently loaded
            if (userSnapshot) {
                fetchUserSnapshot()
            }

            // Refresh the users list
            fetchUsers()
        } catch (err) {
            console.error('Error clearing user data:', err)
            setClearDataError('Failed to clear user data')
        } finally {
            setClearDataLoading(false)
        }
    }



    return (
        <Box>
            <Typography variant="h4" gutterBottom>
                Debug Settings
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Configure debug values for specific ASTA sessions. This screen is only available in debug mode.
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    {success}
                </Alert>
            )}

            <Paper sx={{ p: 3 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel>Select Session ID</InputLabel>
                            <Select
                                value={selectedSessionId}
                                onChange={(e) => setSelectedSessionId(e.target.value)}
                                label="Select Session ID"
                            >
                                {recentSessions.map((session) => (
                                    <MenuItem key={session.session_id} value={session.session_id}>
                                        {session.session_id.substring(0, 8)}... ({new Date(session.created_at).toLocaleDateString()})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={useNullDay}
                                    onChange={(e) => setUseNullDay(e.target.checked)}
                                />
                            }
                            label="Set current_day to null"
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                label="Current Day"
                                value={currentDay}
                                onChange={setCurrentDay}
                                disabled={useNullDay}
                                renderInput={(params) => <TextField {...params} fullWidth />}
                            />
                        </LocalizationProvider>
                    </Grid>

                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button
                                variant="contained"
                                onClick={handleSaveDebugValues}
                                disabled={loading || !selectedSessionId}
                            >
                                {loading ? <CircularProgress size={20} /> : 'Save Debug Values'}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={handleClearDebugValues}
                                disabled={loading || !selectedSessionId}
                            >
                                {loading ? <CircularProgress size={20} /> : 'Clear Debug Values'}
                            </Button>

                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {selectedSessionId && (
                <Paper sx={{ p: 3, mt: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Current Debug Values
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Session ID: {selectedSessionId}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Current Day: {useNullDay ? 'null' : (currentDay ? currentDay.toISOString() : 'Not set')} (UTC)
                    </Typography>
                    {currentDebugValues && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Raw JSON: {JSON.stringify(currentDebugValues)}
                        </Typography>
                    )}
                </Paper>
            )}

            {/* User Snapshot Section */}
            <Paper sx={{ p: 3, mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                    User Snapshot
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Fetch and display a complete user snapshot including profile and health data.
                </Typography>

                {snapshotError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {snapshotError}
                    </Alert>
                )}

                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel>Select User</InputLabel>
                            <Select
                                value={selectedUserId}
                                onChange={(e) => setSelectedUserId(e.target.value)}
                                label="Select User"
                                disabled={usersLoading}
                            >
                                <MenuItem value="">
                                    <em>Select a user...</em>
                                </MenuItem>
                                {users.map((user) => (
                                    <MenuItem key={user.id} value={user.id}>
                                        {user.name || 'Unknown'} ({user.id.substring(0, 8)}...)
                                    </MenuItem>
                                ))}
                            </Select>
                            {usersLoading && (
                                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                    <CircularProgress size={16} sx={{ mr: 1 }} />
                                    <Typography variant="caption" color="text.secondary">
                                        Loading users...
                                    </Typography>
                                </Box>
                            )}
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <Button
                                variant="contained"
                                onClick={fetchUserSnapshot}
                                disabled={snapshotLoading || !selectedUserId}
                            >
                                {snapshotLoading ? <CircularProgress size={20} /> : 'Fetch Snapshot'}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setUserSnapshot(null)
                                    setSnapshotError(null)
                                    setSelectedUserId('')
                                }}
                                disabled={snapshotLoading}
                            >
                                Clear
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={() => setClearDataDialogOpen(true)}
                                disabled={!selectedUserId}
                            >
                                Clear User Data
                            </Button>
                        </Box>
                    </Grid>
                </Grid>

                {userSnapshot && (
                    <Box>
                        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                            Snapshot Results
                        </Typography>

                        {/* Profile Section */}
                        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Profile
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2">
                                        <strong>ID:</strong> {userSnapshot.profile.id}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Name:</strong> {userSnapshot.profile.name}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Email:</strong> {userSnapshot.profile.email}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2">
                                        <strong>Diagnosis:</strong> {userSnapshot.profile.diagnosis_type || 'Not set'}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Treatment:</strong> {userSnapshot.profile.treatment_type || 'Not set'}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Intervention:</strong> {userSnapshot.profile.intervention_id || 'Not set'}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Paper>

                        {/* Health Data Section */}
                        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Health Data ({userSnapshot.journeyMilestones.length})
                            </Typography>
                            {userSnapshot.journeyMilestones.length > 0 ? (
                                userSnapshot.journeyMilestones.map((entry, index) => (
                                    <Box key={entry.id || index} sx={{ mb: 1, p: 1, bgcolor: 'grey.50' }}>
                                        <Typography variant="body2">
                                            <strong>{entry.type}</strong> - {entry.date}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {entry.description}
                                        </Typography>
                                        {entry.treatment_description && (
                                            <Typography variant="body2" color="text.secondary">
                                                Treatment: {entry.treatment_description}
                                            </Typography>
                                        )}
                                        {entry.diagnosis_description && (
                                            <Typography variant="body2" color="text.secondary">
                                                Diagnosis: {entry.diagnosis_description}
                                            </Typography>
                                        )}
                                    </Box>
                                ))
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No health data entries found
                                </Typography>
                            )}
                        </Paper>

                        {/* Raw JSON Section */}
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Raw JSON Data
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={8}
                                value={JSON.stringify(userSnapshot, null, 2)}
                                InputProps={{
                                    readOnly: true,
                                    style: { fontFamily: 'monospace', fontSize: '0.875rem' }
                                }}
                            />
                        </Paper>
                    </Box>
                )}
            </Paper>

            {/* Clear User Data Confirmation Dialog */}
            <Dialog
                open={clearDataDialogOpen}
                onClose={() => setClearDataDialogOpen(false)}
                aria-labelledby="clear-user-data-dialog-title"
                aria-describedby="clear-user-data-dialog-description"
            >
                <DialogTitle id="clear-user-data-dialog-title">
                    Clear User Data
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="clear-user-data-dialog-description">
                        This action will permanently delete all data for the selected user including:
                        <br />• Health data
                        <br />• Assessment results
                        <br />• Runtime pathway and rule execution data
                        <br /><br />
                        The user profile will be reset to:
                        <br />• Name: "unknown"
                        <br />• Age: null
                        <br />• Sex: null
                        <br />• User type: "patient"
                        <br />• Onboard status: "NEW"
                        <br /><br />
                        <strong>This action cannot be undone!</strong>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setClearDataDialogOpen(false)}
                        disabled={clearDataLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleClearUserData}
                        color="error"
                        variant="contained"
                        disabled={clearDataLoading}
                    >
                        {clearDataLoading ? <CircularProgress size={20} /> : 'Clear All Data'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Clear User Data Success/Error Messages */}
            {clearDataSuccess && (
                <Alert severity="success" sx={{ mt: 2 }}>
                    {clearDataSuccess}
                </Alert>
            )}
            {clearDataError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {clearDataError}
                </Alert>
            )}

        </Box>
    )
}

export default Debug 